// Static configuration for Bank account history.

import type { BankHistoryFilter } from "./types";

export const BANK_HISTORY_COL_WIDTHS: Record<string, number | null> = {
  date: 110,
  refType: 140,
  payeeAccount: null,   // flex
  memo: null,           // flex
  payment: 130,
  deposit: 130,
  attachments: 50,
  check: 44,
  balance: 150,
};

export const RECONCILE_STATUS_OPTIONS = [
  { value: "all", label: "All" },
  { value: "reconciled", label: "Reconciled" },
  { value: "cleared", label: "Cleared" },
  { value: "unreconciled", label: "Unreconciled" },
];

export const TRANSACTION_TYPE_OPTIONS = [
  { value: "all", label: "All" },
  { value: "payment", label: "Payment" },
  { value: "cheque", label: "Cheque" },
  { value: "deposit", label: "Deposit" },
  { value: "transfer", label: "Transfer" },
  { value: "debit", label: "Debit" },
  { value: "payroll", label: "Payroll" },
  { value: "interest", label: "Interest" },
  { value: "bank-fee", label: "Bank fee" },
];

export const DATE_PRESET_OPTIONS = [
  { value: "all", label: "All dates" },
  { value: "this-month", label: "This month" },
  { value: "last-month", label: "Last month" },
  { value: "this-quarter", label: "This quarter" },
  { value: "this-year", label: "This year" },
  { value: "custom", label: "Custom" },
];

export const DEFAULT_BANK_HISTORY_FILTER: BankHistoryFilter = {
  find: "",
  reconcileStatus: "all",
  transactionType: "all",
  payee: "all",
  datePreset: "all",
  fromDate: "",
  toDate: "",
};

export const PAGE_SIZE = 50;

// ============================================================
// Column layout that switches per account type.
// ============================================================

export interface ColumnLayout {
  leftLabel: string;
  leftFieldKey: "payment" | "deposit";
  rightLabel: string;
  rightFieldKey: "payment" | "deposit";
  balanceLabel: string;
}

export function getColumnLayout(accountType?: string): ColumnLayout {
  switch (accountType) {
    case "Accounts Receivable":
    case "Accounts Payable":
      return { leftLabel: "Charge / Credit", leftFieldKey: "deposit", rightLabel: "Payment", rightFieldKey: "payment", balanceLabel: "Open Balance" };
    case "Credit Card":
      return { leftLabel: "Charge", leftFieldKey: "payment", rightLabel: "Payment", rightFieldKey: "deposit", balanceLabel: "Balance" };
    case "Other Current Asset":
    case "Fixed Asset":
      return { leftLabel: "Decrease", leftFieldKey: "payment", rightLabel: "Increase", rightFieldKey: "deposit", balanceLabel: "Balance" };
    case "Other Current Liability":
    case "Sub-account of Payroll liabilities":
      return { leftLabel: "Increase", leftFieldKey: "deposit", rightLabel: "Decrease", rightFieldKey: "payment", balanceLabel: "Balance" };
    case "Bank":
    default:
      return { leftLabel: "Payment", leftFieldKey: "payment", rightLabel: "Deposit", rightFieldKey: "deposit", balanceLabel: "Balance" };
  }
}

// ============================================================
// User-configurable table settings, persisted in localStorage.
// ============================================================

export interface BankHistorySettings {
  columns: {
    memo: boolean;
    reconcileStatus: boolean;
    attachments: boolean;
    runningBalance: boolean;
  };
  showInOneLine: boolean;
  paperLedgerMode: boolean;
  compact: boolean;
  rowsPerPage: 50 | 100 | 200;
}

export const DEFAULT_BANK_HISTORY_SETTINGS: BankHistorySettings = {
  columns: {
    memo: true,
    reconcileStatus: true,
    attachments: false,
    runningBalance: true,
  },
  showInOneLine: false,
  paperLedgerMode: false,
  compact: false,
  rowsPerPage: 50,
};

const SETTINGS_STORAGE_KEY = "novala.bankHistory.settings.v1";

export function loadBankHistorySettings(): BankHistorySettings {
  if (typeof window === "undefined") return DEFAULT_BANK_HISTORY_SETTINGS;
  try {
    const raw = window.localStorage.getItem(SETTINGS_STORAGE_KEY);
    if (!raw) return DEFAULT_BANK_HISTORY_SETTINGS;
    const parsed = JSON.parse(raw);
    return {
      ...DEFAULT_BANK_HISTORY_SETTINGS,
      ...parsed,
      columns: { ...DEFAULT_BANK_HISTORY_SETTINGS.columns, ...(parsed.columns ?? {}) },
    };
  } catch {
    return DEFAULT_BANK_HISTORY_SETTINGS;
  }
}

export function saveBankHistorySettings(s: BankHistorySettings): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(SETTINGS_STORAGE_KEY, JSON.stringify(s));
  } catch {
    // ignore quota or private-mode errors
  }
}